import { useGenerateScoresQuery } from '@apps/materiality-tracker/api/materiality-assessment';
import { useRegenerateMetricGroup } from './hooks/useRegenerateMetricGroup';
import { PACK } from '@constants/terminology';
import { BasicAlert } from '@g17eco/molecules/alert';
import { JobStatus } from '@g17eco/types/background-jobs';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { useToggle } from '@hooks/useToggle';
import { skipToken } from '@reduxjs/toolkit/query';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { isAfter } from '@utils/date';

const RegenerateModal = ({
  isOpen,
  toggle,
  handleRegenerate,
}: {
  isOpen: boolean;
  toggle: () => void;
  handleRegenerate: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>
        <span className='text-ThemeWarningExtradark'>Regenerate {PACK.SINGULAR}</span>
      </ModalHeader>
      <ModalBody>
        <p className='text-ThemeTextMedium'>
          Changes have been made to the underlining material topics relevant to this {PACK.SINGULAR}, however the{' '}
          {PACK.SINGULAR} has not been update to reflect these changes. Would you like to regenerate the topics mapped
          within this {PACK.SINGULAR}?
        </p>
        <p className='fst-italic text-ThemeTextMedium mb-0'>
          Please note: Regenerating the {PACK.SINGULAR} will overwrite any previous edits you have made to it.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' onClick={toggle}>
          Cancel
        </Button>

        <Button color='warning' onClick={handleRegenerate}>
          Regenerate {PACK.SINGULAR}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const GENERAL_REQUIREMENTS_SUBGROUP = {
  groupName: 'General Requirements',
};

export const OutdatedMetricGroupAlert = ({ metricGroup }: { metricGroup: MetricGroup }) => {
  const initiativeId = metricGroup.initiativeId;
  const assessmentId = metricGroup.survey?._id;
  const topTopicsCount = metricGroup.source?.topTopicsCount;

  const { data: latestScoreJob, isFetching: isLoadingScoreJob } = useGenerateScoresQuery(
    initiativeId && assessmentId ? { initiativeId, surveyId: assessmentId } : skipToken,
  );

  const [regenerateModal, toggleRegenerateModal, setRegenerateModal] = useToggle(false);

  const { handleRegenerate } = useRegenerateMetricGroup({
    callback: () => setRegenerateModal(false),
  });

  // Legacy MT metric groups were not generated with pillar-based subgroups — flag for update
  const isOutdated =
    !metricGroup.source?.jobId ||
    !metricGroup.subgroups ||
    !metricGroup.subgroups.some((g) => g.groupName === GENERAL_REQUIREMENTS_SUBGROUP.groupName) ||
    (latestScoreJob?.status === JobStatus.Completed && isAfter(metricGroup.updated, latestScoreJob.updatedAt));

  if (isLoadingScoreJob || !isOutdated) {
    return null;
  }

  return (
    <>
      <BasicAlert type='warning' className='d-flex justify-content-between align-items-center'>
        <div>
          <i className='fas fa-circle-info text-ThemeWarningExtradark mr-1' /> Topics for this {PACK.SINGULAR} have
          changed, but the {PACK.SINGULAR} remain unchanged. Would you like to regenerate this {PACK.SINGULAR}?
        </div>
        <Button color='warning' size='sm' onClick={() => setRegenerateModal(true)}>
          <i className='fal fa-arrows-rotate mr-2' />
          Regenerate
        </Button>
      </BasicAlert>
      <RegenerateModal
        isOpen={regenerateModal}
        toggle={toggleRegenerateModal}
        handleRegenerate={() => handleRegenerate(metricGroup, topTopicsCount)}
      />
    </>
  );
};
